import { premiumReportPlanMap } from "@ishvaram/domain-data";
import { reportSalesCopyByPlanId } from "@ishvaram/domain-data/report-sales-copy";
import type { SEOReportPlanId } from "@/lib/seo-intents";

export interface ReportContent {
  slug: string;
  plan_id: SEOReportPlanId;
  title_en: string;
  meta_description: string;
  hero_pain: string;
  hero_vedic: string;
  price_inr: number;
  strike_price_inr?: number;
  /** Catalog-backed page floor; the backend quality gate owns delivery. */
  pdf_pages_floor: number;
  what_inside: readonly string[];
  why_now: string;
  trust_lines: readonly string[];
  faqs: readonly { readonly q: string; readonly a: string }[];
}

type ReportSalesCopyPlanId = keyof typeof reportSalesCopyByPlanId;

// Marketing anchors are display policy, not report copy or charged prices. The
// charged price and page floor below always come from the governed catalog map.
const STRIKE_PRICE_BY_PLAN_ID: Partial<Record<ReportSalesCopyPlanId, number>> = {
  brihat_kundali: 999,
  career_report: 499,
  marriage_report: 499,
  annual_forecast: 499,
  muhurat_report: 999,
  dosha_remedy_plan: 999,
  sade_sati: 499,
  pitra_dosh: 499,
  mahadasha_forecast: 499,
  numerology_report: 499,
  gemstone_report: 599,
  baby_name_report: 599,
  lal_kitab_report: 499,
  transit_forecast: 599,
};

function toReportContent(planId: ReportSalesCopyPlanId): ReportContent {
  const copy = reportSalesCopyByPlanId[planId];
  const plan = premiumReportPlanMap[planId];
  return {
    slug: copy.content_slug,
    plan_id: planId,
    title_en: copy.en.title,
    meta_description: copy.meta_description,
    hero_pain: copy.en.hero_pain,
    hero_vedic: copy.en.hero_vedic,
    price_inr: plan.price_inr,
    strike_price_inr: STRIKE_PRICE_BY_PLAN_ID[planId],
    pdf_pages_floor: plan.pages,
    what_inside: copy.en.what_inside,
    why_now: copy.en.why_now ?? "",
    trust_lines: copy.en.trust_lines,
    faqs: copy.en.faqs,
  };
}

/**
 * Public report sales pages projected from the Supabase-owned copy mirror.
 * Slugs come from the same row as their copy; prices/pages come from the
 * catalog plan binding, so neither surface can silently drift.
 */
export const REPORT_CONTENT: Record<string, ReportContent> = Object.fromEntries(
  (Object.keys(reportSalesCopyByPlanId) as ReportSalesCopyPlanId[]).map((planId) => {
    const report = toReportContent(planId);
    return [report.slug, report];
  })
);

export const REPORT_SLUGS = Object.keys(REPORT_CONTENT);

/**
 * Governed discovery order for the canonical report catalog.
 *
 * This is presentation policy, so it stays separate from Supabase-owned sales
 * copy and charged-plan facts. Plan IDs are used instead of duplicated slugs;
 * the public href and label are projected from REPORT_CONTENT below. The
 * coverage assertion makes a newly activated report fail closed until it has a
 * place in this ring.
 */
const REPORT_NAVIGATION_RING_PLAN_IDS = [
  "brihat_kundali",
  "career_report",
  "marriage_report",
  "muhurat_report",
  "baby_name_report",
  "numerology_report",
  "gemstone_report",
  "lal_kitab_report",
  "dosha_remedy_plan",
  "pitra_dosh",
  "sade_sati",
  "mahadasha_forecast",
  "transit_forecast",
  "annual_forecast",
] as const satisfies readonly ReportSalesCopyPlanId[];

export const REPORT_RELATED_LINK_COUNT = 6;

function buildReportNavigationRing(): readonly ReportContent[] {
  const ringPlanIds = new Set<string>(REPORT_NAVIGATION_RING_PLAN_IDS);
  const catalogPlanIds = Object.keys(reportSalesCopyByPlanId);
  const hasCompleteCoverage =
    ringPlanIds.size === REPORT_NAVIGATION_RING_PLAN_IDS.length &&
    ringPlanIds.size === catalogPlanIds.length &&
    catalogPlanIds.every((planId) => ringPlanIds.has(planId));

  if (!hasCompleteCoverage) {
    throw new Error("Report navigation ring must cover every active report plan exactly once");
  }
  if (REPORT_RELATED_LINK_COUNT >= REPORT_NAVIGATION_RING_PLAN_IDS.length) {
    throw new Error("Report navigation ring must leave the current report out of its siblings");
  }

  return REPORT_NAVIGATION_RING_PLAN_IDS.map(
    (planId) => REPORT_CONTENT[reportSalesCopyByPlanId[planId].content_slug]
  );
}

export const REPORT_NAVIGATION_RING = buildReportNavigationRing();

/**
 * Three neighbors on either side of the current report, interleaved nearest
 * first. The balanced ring gives every report the same six outgoing and six
 * incoming product links without turning the page into a second offer grid.
 */
export function relatedReportsForSlug(slug: string): readonly ReportContent[] {
  const currentIndex = REPORT_NAVIGATION_RING.findIndex((report) => report.slug === slug);
  if (currentIndex < 0) throw new Error(`Unknown report slug: ${slug}`);

  const ringSize = REPORT_NAVIGATION_RING.length;
  return Array.from({ length: REPORT_RELATED_LINK_COUNT }, (_, offsetIndex) => {
    const distance = Math.floor(offsetIndex / 2) + 1;
    const offset = offsetIndex % 2 === 0 ? -distance : distance;
    const index = (currentIndex + offset + ringSize) % ringSize;
    return REPORT_NAVIGATION_RING[index];
  });
}

/** Guard-backed "N+ page" floor for a plan, for offer surfaces. */
export function pagesFloorForPlan(planId: string): number | null {
  const entry = Object.values(REPORT_CONTENT).find((report) => report.plan_id === planId);
  return entry?.pdf_pages_floor ?? null;
}
